import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getIsLoggedIn, getUserInfo } from '@store/auth/auth-selectors';
import { setCompanyDataAttribute } from '@utils/companyTheme';

type CompanyMode = 'default' | 'ingos' | 'reso';
const SESSION_THEME_KEY = 'session_company_theme_key';

export const useCompanyTheme = (): string | null => {
  const user = useSelector(getUserInfo);
  const isLoggedIn = useSelector(getIsLoggedIn);

  // CRA: только REACT_APP_* доступно в process.env на клиенте [web:31]
  const companyMode = (process.env.REACT_APP_COMPANY_MODE ?? 'default') as CompanyMode;

  const domCompanyKey = useMemo(() => {
    if (companyMode === 'ingos') return 'ingosstrakh'; // без логина
    if (companyMode === 'reso') return 'reso'; // без логина
    if (!isLoggedIn) return null;

    const savedTheme = sessionStorage.getItem(SESSION_THEME_KEY);
    if (savedTheme) return savedTheme;

    const initialTheme = user?.active_policy?.company_key ?? null;
    if (initialTheme) {
      sessionStorage.setItem(SESSION_THEME_KEY, initialTheme);
    }
    return initialTheme;
  }, [companyMode, isLoggedIn, user?.active_policy?.company_key]);

  // Keep ingos business branches (company === 'ingosstrakh') compatible for reso build.
  const compatibilityThemeKey = domCompanyKey === 'reso' ? 'ingosstrakh' : domCompanyKey;

  useEffect(() => {
    if (!isLoggedIn) {
      sessionStorage.removeItem(SESSION_THEME_KEY);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (domCompanyKey) {
      setCompanyDataAttribute(domCompanyKey);
    } else if (!isLoggedIn) {
      setCompanyDataAttribute(null);
    }
  }, [domCompanyKey, isLoggedIn]);

  return compatibilityThemeKey;
};
